import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import CTABanner from "../components/CTABanner";
import Lightbox from "../components/Lightbox";
import { IconStar, IconQuote, IconArrow } from "../components/icons";
import { TESTIMONIALS } from "../data";
import heroGallery from "../assets/images/hero/hero-gallery.png";
import gallery1 from "../assets/images/gallery/gallery-1.jpeg";
import gallery2 from "../assets/images/gallery/gallery-2.jpeg";
import gallery3 from "../assets/images/gallery/gallery-3.MOV";
import gallery4 from "../assets/images/gallery/gallery-4.jpeg";
import gallery5 from "../assets/images/gallery/gallery-5.jpeg";
import gallery6 from "../assets/images/gallery/gallery-6.MOV";
import gallery7 from "../assets/images/gallery/gallery-7.MOV";
import gallery8 from "../assets/images/gallery/gallery-8.jpeg";
import gallery9 from "../assets/images/gallery/gallery-9.jpeg";
import gallery10 from "../assets/images/gallery/gallery-10.jpeg";
import gallery11 from "../assets/images/gallery/gallery-11.MOV";
import gallery12 from "../assets/images/gallery/gallery-12.jpeg";

const GALLERY_ITEMS = [
  { src: gallery1,  alt: "Hen party cocktail class in full swing",     type: "image" },
  { src: gallery2,  alt: "Beautiful cocktail setup for a birthday",     type: "image" },
  { src: gallery3,  alt: "Corporate team building cocktail event",      type: "video" },
  { src: gallery4,  alt: "Guests shaking cocktails and laughing",       type: "image" },
  { src: gallery5,  alt: "Mobile service at your private location",     type: "image" },
  { src: gallery6,  alt: "Colourful cocktails lined up",                type: "video" },
  { src: gallery7,  alt: "Group photo with cocktails at a hen do",      type: "video" },
  { src: gallery8,  alt: "Professional bartender crafting drinks",      type: "image" },
  { src: gallery9,  alt: "Behind the scenes cocktail moments",          type: "image" },
  { src: gallery10, alt: "Vibrant cocktail creations at a party",       type: "image" },
  { src: gallery11, alt: "Guests enjoying cocktail masterclass",        type: "video" },
  { src: gallery12, alt: "Colourful cocktail spread at an event",       type: "image" },
];

const PlayIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
    <circle cx="22" cy="22" r="22" fill="rgba(0,0,0,0.45)" />
    <polygon points="17,13 35,22 17,31" fill="white" />
  </svg>
);

export default function Gallery() {
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      <Lightbox images={GALLERY_ITEMS} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNav={setLightboxIndex} />

      {/* Hero */}
      <section className="sb-noise" style={{
        position: "relative", overflow: "hidden",
        backgroundImage: `url(${heroGallery})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "clamp(60px, 10vw, 100px) 0 clamp(48px, 8vw, 80px)", textAlign: "center",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, rgba(58,24,68,0.75), rgba(80,30,60,0.68))",
          zIndex: 0,
        }} />
        <div className="sb-container" style={{ position: "relative", zIndex: 2 }}>
          <Reveal>
            <span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold-light)", marginBottom: "12px" }}>Gallery & Reviews</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 style={{ color: "var(--white)", maxWidth: "700px", margin: "0 auto 16px" }}>
              See the <span style={{ color: "var(--gold)" }}>Magic</span> in Action
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p style={{ color: "rgba(255,255,255,0.65)", maxWidth: "540px", margin: "0 auto", fontSize: "1.05rem" }}>
              Real events, real cocktails, real smiles. Browse our gallery and hear what our guests have to say.
            </p>
          </Reveal>
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to top, var(--blush-tint), transparent)" }} />
      </section>

      {/* Gallery Grid */}
      <section className="sb-section">
        <div className="sb-container">
          <SectionHeader label="Our Gallery" title="Moments Worth Sharing" subtitle="A glimpse into the cocktail experiences we've created for our clients." />
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
            gap: "16px",
          }}>
            {GALLERY_ITEMS.map(({ src, alt, type }, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div
                  onClick={() => setLightboxIndex(i)}
                  style={{
                    position: "relative", borderRadius: "var(--radius-md)", overflow: "hidden",
                    transition: "transform 0.35s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "scale(1.02)";
                    if (type === "video") {
                      e.currentTarget.querySelector("video")?.play();
                      const overlay = e.currentTarget.querySelector(".vid-play");
                      if (overlay) overlay.style.opacity = "0";
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "scale(1)";
                    if (type === "video") {
                      const v = e.currentTarget.querySelector("video");
                      if (v) { v.pause(); v.currentTime = 0; }
                      const overlay = e.currentTarget.querySelector(".vid-play");
                      if (overlay) overlay.style.opacity = "1";
                    }
                  }}
                >
                  {type === "video" ? (
                    <>
                      <video
                        src={src}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        style={{
                          display: "block", width: "100%",
                          aspectRatio: "1/1",
                          objectFit: "cover",
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        className="vid-play"
                        style={{
                          position: "absolute", inset: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "opacity 0.3s ease",
                          pointerEvents: "none",
                        }}
                      >
                        <PlayIcon />
                      </div>
                    </>
                  ) : (
                    <img
                      src={src}
                      alt={alt}
                      loading="lazy"
                      style={{
                        display: "block", width: "100%",
                        aspectRatio: "1/1",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="sb-section" style={{ background: "var(--blush-glow)" }}>
        <div className="sb-container">
          <SectionHeader label="Client Reviews" title="What Our Guests Say" subtitle="We're proud of every event we deliver. Here's what our clients think." />

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "24px", marginBottom: "36px" }}>
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div style={{
                  background: "var(--white)", borderRadius: "var(--radius-md)", padding: "28px",
                  boxShadow: "var(--shadow-sm)", border: "1px solid rgba(212,168,83,0.08)",
                  position: "relative", height: "100%",
                }}>
                  <span style={{ position: "absolute", top: "16px", right: "20px", color: "var(--gold)" }}><IconQuote /></span>
                  <div style={{ display: "flex", gap: "2px", marginBottom: "14px" }}>
                    {[...Array(t.rating)].map((_, j) => <span key={j} style={{ color: "var(--gold)" }}><IconStar /></span>)}
                  </div>
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", marginBottom: "16px", fontStyle: "italic", lineHeight: 1.7 }}>
                    "{t.text}"
                  </p>
                  <span style={{ fontWeight: 600, color: "var(--plum)", fontSize: "0.9rem" }}>— {t.name}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div style={{
              marginTop: "clamp(28px, 4vw, 40px)",
              background: "var(--white)", borderRadius: "var(--radius-lg)",
              padding: "clamp(24px, 4vw, 36px)", textAlign: "center",
              boxShadow: "var(--shadow-sm)", border: "1px solid rgba(212,168,83,0.12)",
            }}>
              <div style={{ display: "flex", gap: "2px", justifyContent: "center", marginBottom: "12px", color: "var(--gold)" }}>
                {[...Array(5)].map((_, j) => <IconStar key={j} />)}
              </div>
              <h3 style={{ color: "var(--plum)", marginBottom: "8px" }}>
                Celebrated with us recently?
              </h3>
              <p style={{ color: "var(--text-secondary)", maxWidth: "480px", margin: "0 auto 20px", fontSize: "0.98rem" }}>
                Your review helps other guests pick the perfect experience — and makes a small team very happy. Takes under a minute.
              </p>
              <button
                className="sb-btn sb-btn-primary"
                onClick={() => { navigate("/leave-review"); window.scrollTo({ top: 0 }); }}
                style={{ justifyContent: "center" }}
              >
                Leave a Review <IconArrow />
              </button>
            </div>
          </Reveal>

        </div>
      </section>

      <CTABanner headline="Inspired? Let's Plan Your Event" sub="Join our community of happy guests who've celebrated with Silly Butterfly." btnText="Plan Your Event" />
    </main>
  );
}
